import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CreatorDashboardShell } from "@/components/CreatorDashboardShell";
import { AdminOverviewPanel } from "@/components/admin/AdminOverviewPanel";
import { AdminCreatorsPanel } from "@/components/admin/AdminCreatorsPanel";
import { AdminAccountsPanel } from "@/components/admin/AdminAccountsPanel";
import { AdminPlatformsPanel } from "@/components/admin/AdminPlatformsPanel";
import { AdminActivityPanel } from "@/components/admin/AdminActivityPanel";
import { api, ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { env } from "@/lib/env";
import {
  ADMIN_NAV,
  type AdminAccount,
  type AdminEpisode,
  type AdminOverview,
  type AdminPlatformRow,
  type AdminTab,
  type AdminUser,
} from "@/lib/admin-types";
import { Route as AdminRoute } from "./admin";

export const Route = createFileRoute("/admin/")({
  head: () => ({
    meta: [
      { title: "Admin — AutoTubber" },
      {
        name: "description",
        content:
          "AutoTubber admin dashboard: creators, connected platforms, and episode analytics.",
      },
    ],
  }),
  component: AdminDashboard,
});

function AdminDashboard() {
  const navigate = useNavigate();
  const { tab: tabFromSearch } = AdminRoute.useSearch();
  const { ready, user } = useAuth();
  const [activeTab, setActiveTab] = useState<AdminTab>("overview");
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState<AdminOverview | null>(null);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [accounts, setAccounts] = useState<AdminAccount[]>([]);
  const [platforms, setPlatforms] = useState<AdminPlatformRow[]>([]);
  const [episodes, setEpisodes] = useState<AdminEpisode[]>([]);
  const [userQuery, setUserQuery] = useState("");

  useEffect(() => {
    if (!ready) return;
    if (!user) {
      navigate({
        to: env.routes.login,
        search: { redirect: env.routes.admin },
        replace: true,
      });
      return;
    }
    if (user.role !== "ADMIN") {
      toast.error("Admin access required");
      navigate({ to: env.routes.dashboard, replace: true });
    }
  }, [ready, user, navigate]);

  useEffect(() => {
    const allowed = ADMIN_NAV.map((n) => n.tab);
    if (tabFromSearch && allowed.includes(tabFromSearch as AdminTab)) {
      setActiveTab(tabFromSearch as AdminTab);
    }
  }, [tabFromSearch]);

  const onTabChange = (tab: string) => {
    const next = tab as AdminTab;
    setActiveTab(next);
    navigate({
      to: "/admin",
      search: { tab: next },
      replace: true,
    });
  };

  const load = useCallback(async (opts?: { silent?: boolean }) => {
    if (!opts?.silent) setLoading(true);
    try {
      const [ov, u, a, p, e] = await Promise.all([
        api<AdminOverview>(env.paths.adminOverview),
        api<{ users: AdminUser[] }>(env.paths.adminUsers),
        api<{ accounts: AdminAccount[] }>(env.paths.adminAccounts),
        api<{ platforms: AdminPlatformRow[] }>(env.paths.adminPlatforms),
        api<{ episodes: AdminEpisode[] }>(env.paths.adminEpisodes),
      ]);
      setOverview(ov);
      setUsers(u.users);
      setAccounts(a.accounts);
      setPlatforms(p.platforms);
      setEpisodes(e.episodes);
    } catch (err) {
      if (!opts?.silent) {
        toast.error(
          err instanceof ApiError ? err.message : "Failed to load admin data",
        );
      }
    } finally {
      if (!opts?.silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!ready || !user || user.role !== "ADMIN") return;
    void load();
  }, [ready, user, load]);

  // Live refresh for overview throughput chart
  useEffect(() => {
    if (!ready || !user || user.role !== "ADMIN") return;
    if (activeTab !== "overview") return;
    const id = window.setInterval(() => {
      void load({ silent: true });
    }, 20_000);
    return () => window.clearInterval(id);
  }, [ready, user, activeTab, load]);

  if (!ready || !user || user.role !== "ADMIN") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background text-sm text-muted-foreground">
        Checking admin access…
      </div>
    );
  }

  return (
    <CreatorDashboardShell
      navItems={[...ADMIN_NAV]}
      activeTab={activeTab}
      onTabChange={onTabChange}
      userEmail={user.email}
      brandSuffix="Admin"
      mobileTitle="Admin dashboard"
    >
      {activeTab === "overview" && (
        <AdminOverviewPanel overview={overview} loading={loading} />
      )}
      {activeTab === "creators" && (
        <AdminCreatorsPanel
          users={users}
          query={userQuery}
          onQueryChange={setUserQuery}
        />
      )}
      {activeTab === "accounts" && (
        <AdminAccountsPanel accounts={accounts} />
      )}
      {activeTab === "platforms" && (
        <AdminPlatformsPanel platforms={platforms} />
      )}
      {activeTab === "activity" && (
        <AdminActivityPanel episodes={episodes} />
      )}
    </CreatorDashboardShell>
  );
}
